<script setup>
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { ErrorMessage, Field } from "vee-validate";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["update:modelValue"]);

const { t } = useI18n();

const props = defineProps({
  modelValue: {
    required: true,
  },
  fieldId: {
    type: String,
    required: true,
  },
  fieldName: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  required: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasEmptyOption: {
    type: Boolean,
    required: false,
    default: false,
  },
  inlineLabel: {
    type: Boolean,
    required: false,
    default: false,
  },
  size: {
    type: String,
    required: false,
    default: "medium",
  },
  maxDate: {
    type: Date,
    required: false,
    default: null,
  },
});

const monthAndYearRef = ref(null);
</script>

<template>
  <div
    class="mb-4 flex"
    :class="{
      'flex-row items-center': props.inlineLabel,
      'flex-col': !props.inlineLabel,
    }"
  >
    <label
      :for="props.fieldName"
      class="whitespace-nowrap"
      :class="{
        'mr-2': props.inlineLabel,
        'mb-2': !props.inlineLabel,
        'text-sm': props.size === 'small',
      }"
    >
      {{ props.label }}
      <sup v-if="props.required" class="text-red-500">*</sup>
    </label>

    <Field
      :id="props.fieldId"
      :name="props.fieldName"
      :modelValue="props.modelValue"
    >
      <VueDatePicker
        ref="monthAndYearRef"
        :model-value="props.modelValue"
        month-picker
        :max-date="props.maxDate"
        locale="fr"
        class="month-and-year-field"
        :class="{
          'small-size': props.size === 'small',
        }"
        :hide-navigation="['year']"
        auto-apply
        @update:model-value="emit('update:modelValue', $event)"
      >
        <template #dp-input="{ isMenuOpen }">
          <button
            class="flex w-full items-center gap-2 rounded border border-gray-300 ring-1 ring-transparent duration-150 focus:outline-0 focus:ring-gray-300"
            :class="{
              '!ring-gray-300': isMenuOpen,
              'ring-transparent': !isMenuOpen,
              'min-w-[210px] px-5 py-3': props.size === 'medium',
              'h-[28px] min-w-[140px] px-3 py-1 text-sm':
                props.size === 'small',
            }"
          >
            <template
              v-if="
                props.modelValue &&
                props.modelValue.month !== null &&
                props.modelValue.year !== null
              "
            >
              <span class="capitalize">
                {{ t("all.label.month" + props.modelValue.month) }}&nbsp;{{
                  props.modelValue.year
                }}
              </span>
            </template>
            <template v-else> &nbsp;</template>

            <ChevronDownIcon
              class="ml-auto size-4 text-gray-500 duration-150"
              :class="{ 'rotate-180 transform': isMenuOpen }"
            />
          </button>
        </template>

        <template v-if="props.hasEmptyOption && props.modelValue" #action-extra>
          <ActionButton
            :callback="
              () => {
                emit('update:modelValue', null);
                monthAndYearRef.closeMenu();
              }
            "
            class="w-full"
          >
            {{ t("all.button.clear") }}
          </ActionButton>
        </template>

        <template #arrow-left>
          <ChevronLeftIcon class="size-5" />
        </template>

        <template #arrow-right>
          <ChevronRightIcon class="size-5" />
        </template>

        <template #clear-icon></template>

        <template #action-preview></template>
      </VueDatePicker>
    </Field>

    <ErrorMessage :name="props.fieldName" class="mt-1 text-sm text-red-500" />
  </div>
</template>

<style>
.month-and-year-field .dp--menu-wrapper {
  top: 3.5rem !important;
  left: 0 !important;
  width: 100%;
}

.month-and-year-field.small-size .dp--menu-wrapper {
  top: 2rem !important;
  left: 0 !important;
  width: auto;
}

.month-and-year-field .dp__menu {
  border: solid rgb(209 213 219) 1px;
  box-shadow:
    0 1px 3px 0 rgb(0 0 0 / 0.1),
    0 1px 2px -1px rgba(0 0 0 / 0.1);
}

.month-and-year-field .dp--menu--inner-stretched {
  padding: 0.5rem 1.25rem 0 1.25rem;
}

.month-and-year-field.small-size .dp--menu--inner-stretched {
  padding: 0.25rem 0.75rem 0 0.75rem;
}

.month-and-year-field .dp__arrow_top {
  display: none;
}

.month-and-year-field .dp__overlay {
  width: 100% !important;
}

.month-and-year-field .dp__overlay_cell_active {
  background-color: #2563eb;
}

.month-and-year-field .dp__overlay_cell:hover,
.month-and-year-field .dp--year-select:hover,
.month-and-year-field .dp__inner_nav:hover {
  background: #eff2f5;
}

.month-and-year-field .dp__overlay_col {
  padding: 0.25rem;
}

.month-and-year-field .dp__overlay_cell {
  border-radius: 0.25rem;
}

.month-and-year-field .dp__overlay_cell_pad {
  padding: 0.5rem;
}

.month-and-year-field .dp__overlay_cell_disabled {
  background: transparent;
  color: #94a3b8;
}

.month-and-year-field .dp__action_extra {
  padding: 0.5rem 1.25rem;
}

.month-and-year-field.small-size .dp__action_extra {
  padding: 0.25rem 0.75rem;
}
</style>
